import { Body2 } from '../../../../atoms/Typography'
import { Box, VStack, HStack, Flex, AbsoluteCenter } from '../../../../atoms/Layout'
import { BiDonateHeartIcon, HeartIcon } from '../../../../atoms/Icons'
import { Tag } from '@web/components/atoms/Tag'
import { useDonationFormContext } from '../../DonationFormProvider'
import { useBrandingsStore } from '@web/components/utils/branding/useBrandingsStore'
import { toDisplayAmountNoDecimal } from '@web/components/utils/format'
import { useColorWithOpacity } from '@web/components/molecules/ColorPicker/utils'
import { useMediaQuery } from '@web/components/utils/chakra-hooks'
import { DESKTOP_CONTAINER_QUERY } from '@web/components/page-components/utils'

type MatchedGivingProps = {
  multiplier: number
}

const MatchedGiving = ({ multiplier }: MatchedGivingProps) => {
  const [isMobile] = useMediaQuery(['(max-width: 900px)'], { fallback: [] })
  const amount = useDonationFormContext(({ formValues }) => formValues.amount || 0)
  const styles = useDonationFormContext(({ settings }) => settings.styles)
  const primaryColorFromBranding = useBrandingsStore(({ colorData }) => colorData['Brand'])
  const backgroundColor = useColorWithOpacity(
    styles?.matchedGivingBackgroundColor,
    styles?.matchedGivingBackgroundColorOpacity,
    '#FFFFFF',
  )
  const accentColor = useColorWithOpacity(
    styles?.matchedGivingAccentColor,
    styles?.matchedGivingAccentColorOpacity,
    styles?.accentColor ?? primaryColorFromBranding,
  )
  const textColor = useColorWithOpacity(styles?.matchedGivingTextColor, '1', '#000000')
  const accentTextColor = useColorWithOpacity(styles?.matchedGivingAccentTextColor, '1', '#FFFFFF')

  const matchedAmount = multiplier * amount

  const heartSize = isMobile ? 8 : Math.min(Math.round(8 + amount / 100), 10)

  return (
    <Box
      w="100%"
      css={{
        overflow: 'hidden',
        '@keyframes beat': {
          '0%': { transform: 'scale(1)' },
          '50%': { transform: 'scale(1.1)' },
          '100%': { transform: 'scale(1)' },
        },
        '@keyframes collapse': {
          '0%': { maxHeight: '0' },
          '100%': { maxHeight: '300px' },
        },
        animation: 'collapse 0.8s ease-in-out',
      }}
    >
      <HStack
        w="100%"
        bgColor={backgroundColor}
        color={textColor}
        borderRadius="6px"
        justifyContent="space-around"
        css={{
          padding: '8px',
          [DESKTOP_CONTAINER_QUERY]: {
            padding: '8px 36px',
            '@container donation-form (width < 400px)': {
              padding: '8px',
            },
          },
        }}
      >
        <VStack>
          <Tag bgColor={accentColor} color={accentTextColor}>
            MATCHED GIVING
          </Tag>
          <HStack w="100%">
            <Body2 whiteSpace="nowrap">You give</Body2>
            <Flex gap="8px" alignItems="center">
              <BiDonateHeartIcon maxW="24px" />
              <Body2 as="strong" fontWeight={600} color={accentColor}>
                ${toDisplayAmountNoDecimal(amount)}
              </Body2>
            </Flex>
          </HStack>
        </VStack>
        <VStack>
          <Body2>We receive</Body2>
          <HStack color={accentColor} w="100%" justifyContent="space-around" minW="115px" minH="36px">
            <Box position="relative">
              <AbsoluteCenter pb="4px" zIndex={1}>
                <Body2 color={accentTextColor}>x{multiplier}</Body2>
              </AbsoluteCenter>
              <Box animation="beat" key={amount}>
                <HeartIcon boxSize={heartSize} transition="all 0.2s ease-in-out" />
              </Box>
            </Box>
            <Body2 as="strong" fontWeight={400}>
              =
            </Body2>
            <Body2 as="strong" fontWeight={600} fontSize={`${heartSize / 8}rem`} transition="all 0.2s ease-in-out">
              ${toDisplayAmountNoDecimal(matchedAmount)}
            </Body2>
          </HStack>
        </VStack>
      </HStack>
    </Box>
  )
}

export default MatchedGiving
